import { InvestorDetailsSummaryTabHouseholdSummaryQuery, PaginateSortDirection } from '@/__generated__/graphql';
import { InvestorDetailsTabsEnum } from '@/components/investors-page/investorDetailsPage/types';
import { Loader } from '@/components/loaders/loader';
import { StickyTableLoader } from '@/components/loaders/stickyTableLoader';
import { initialValuesFromTable } from '@/components/table/constants';
import FilterPage from '@/components/table/filterPage';
import { TableBodyWrapperWithLoading, TableWrapper } from '@/components/table/styles';
import TableHeader from '@/components/table/tableHeader';
import TablePagination from '@/components/table/tablePagination';
import { TableColumns } from '@/components/table/types';
import { IMobileSort } from '@/components/transactions-page/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import useStickyFooter from '@/hooks/useStickyFooter';
import { useQuery } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  allEntitiesSummaryDisregardAllocationTargetsMobileTableSort,
  allEntitiesSummaryMobileTableSort,
  allEntitiesSummaryTableColumns
} from '../../constants';
import { INVESTOR_DETAILS_SUMMARY_TAB_HOUSEHOLD_SUMMARY_QUERY } from '../../queries';
import AllEntitiesSummaryMobileTable from './mobileTable';
import AllEntitiesSummaryTableBody from './tableBody';
import AllEntitiesSummaryTableSumFooter from './tableSumFooter';

const AllEntitiesSummaryTable = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { searchParams, onChangePaginationData, onChangeSearch, onChangeSort, onChangeToggle } = useSearchParamsManager();

  const investorId = params?.investorId || '';
  const backToTitle = searchParams.get('backToTitle') || '';
  const activeTab: InvestorDetailsTabsEnum = (searchParams.get('activeTab') as InvestorDetailsTabsEnum) || InvestorDetailsTabsEnum.SUMMARY;
  const q = searchParams.get('q') || '';
  const limit = searchParams.get('limit') || initialValuesFromTable.initialAmountRowsPerPage[0];
  const offset = searchParams.get('offset') || 0;
  const key = searchParams.get('key') || 'entityName';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';

  const containerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useStickyFooter(containerRef, footerRef);

  const [tableColumns, setTableColumns] = useState<TableColumns[]>(allEntitiesSummaryTableColumns);
  const [tableRows, setTableRows] = useState<InvestorDetailsSummaryTabHouseholdSummaryQuery['investorHouseholdSummary']['data']>([]);
  const [tableTotalRowData, setTableTotalRowData] = useState<
    InvestorDetailsSummaryTabHouseholdSummaryQuery['investorHouseholdSummary']['totals'] | null
  >(null);
  const [showSpecificColumns, setShowSpecificColumns] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [mobileSortItems, setMobileSortItems] = useState<IMobileSort[]>([]);
  const [mobileSort, setMobileSort] = useState(allEntitiesSummaryMobileTableSort[0].title);
  const sortItem = mobileSortItems.find((item) => item.title === mobileSort);
  const mobileSortList = mobileSortItems.map((item) => item.title);

  const {
    data: legalEntitiesSummaryTableData,
    loading: legalEntitiesSummaryTableLoading,
    error: legalEntitiesSummaryTableError,
    refetch: legalEntitiesSummaryTableRefetch
  } = useQuery(INVESTOR_DETAILS_SUMMARY_TAB_HOUSEHOLD_SUMMARY_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !investorId,
    variables: {
      data: {
        id: investorId,
        includeRecommendation: toggle,
        sortBy: key,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        limit: Number(limit),
        offset: Number(offset)
      }
    },
    onCompleted: (data) => {
      const { entityValueTotal, currentNAVTotal, unfundedCommitmentTotal, capacityTotal, ignoreAllocationTargets } =
        data?.investorHouseholdSummary?.totals;
      const tableRowData = data?.investorHouseholdSummary?.data || [];

      setTableRows(tableRowData);
      setTableTotalRowData({
        entityValueTotal,
        capacityTotal,
        currentNAVTotal,
        unfundedCommitmentTotal,
        ignoreAllocationTargets
      });
    },
    onError: (error) => {
      console.error(error);
    }
  });

  useEffect(() => {
    if (!legalEntitiesSummaryTableError && !legalEntitiesSummaryTableLoading && legalEntitiesSummaryTableData) {
      const { data } = legalEntitiesSummaryTableData.investorHouseholdSummary;

      const haveAllEntitiesAllocationTargets = data.filter((entity) => !entity.ignoreAllocationTargets).length;

      setShowSpecificColumns(!!haveAllEntitiesAllocationTargets);

      if (!haveAllEntitiesAllocationTargets) {
        const filteredTableColumns = allEntitiesSummaryTableColumns.filter(
          (column) =>
            column.key !== 'totalEntityValue' &&
            column.key !== 'privateCapitalPercentage' &&
            column.key !== 'targetOverCommitmentRatio' &&
            column.key !== 'capacity'
        );

        setTableColumns(filteredTableColumns);
        setMobileSortItems(allEntitiesSummaryDisregardAllocationTargetsMobileTableSort);
        return;
      }
      setTableColumns(allEntitiesSummaryTableColumns);
      setMobileSortItems(allEntitiesSummaryMobileTableSort);
    }
  }, [legalEntitiesSummaryTableData, legalEntitiesSummaryTableLoading, legalEntitiesSummaryTableError]);

  useEffect(() => {
    if (!isMobile) return;
    legalEntitiesSummaryTableRefetch({
      data: {
        id: investorId,
        includeRecommendation: toggle,
        sortBy: sortItem?.key,
        sortDirection: sortItem?.asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        limit: Number(limit),
        offset: Number(offset)
      }
    });
  }, [mobileSort]);

  const handleEntitySelect = ({ entityId, isGroup }: { entityId: string; isGroup: boolean }) => {
    const queryParams = new URLSearchParams({
      backToTitle,
      activeTab,
      ...(entityId && { isGroup: isGroup ? 'true' : 'false' })
    });
    navigate(`/investors/${investorId}/${entityId}?${queryParams.toString()}`, { replace: true });
  };

  return (
    <>
      <FilterPage
        search={{
          value: q,
          onChange: onChangeSearch,
          placeholder: 'Search'
        }}
        resultsValue={legalEntitiesSummaryTableData?.investorHouseholdSummary?.total || 0}
        refetch={legalEntitiesSummaryTableRefetch}
        toggle={{
          title: 'Include Pending Commitments',
          isActive: toggle,
          onToggle: () => onChangeToggle(toggle, { shouldSaveScrollPosition: false })
        }}
        mobileSort={{
          value: mobileSort,
          onChange: setMobileSort,
          selects: mobileSortList
        }}
        useNewFilters
      />
      {isMobile ? (
        <>
          {legalEntitiesSummaryTableLoading ? (
            <Loader />
          ) : (
            <AllEntitiesSummaryMobileTable
              tableRows={tableRows}
              showSpecificColumns={showSpecificColumns}
              tableTotalRowData={tableTotalRowData}
              handleEntitySelect={handleEntitySelect}
            />
          )}
        </>
      ) : (
        <TableWrapper padding="0">
          <TableHeader refetch={() => {}} columns={tableColumns} savedSort={{ key, asc }} savedSetSort={onChangeSort} />
          {legalEntitiesSummaryTableLoading && <StickyTableLoader />}
          <TableBodyWrapperWithLoading isLoading={legalEntitiesSummaryTableLoading}>
            <AllEntitiesSummaryTableBody
              tableColumns={tableColumns}
              tableRows={tableRows}
              showSpecificColumns={showSpecificColumns}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
              handleEntitySelect={handleEntitySelect}
            />
            <TableSumFooterWrapper ref={footerRef}>
              <AllEntitiesSummaryTableSumFooter
                tableColumns={tableColumns}
                tableTotalRowData={tableTotalRowData}
                showSpecificColumns={showSpecificColumns}
              />
            </TableSumFooterWrapper>
            <div ref={containerRef}></div>
          </TableBodyWrapperWithLoading>
        </TableWrapper>
      )}
      <PaginationWrap>
        <TablePagination
          savePagination={onChangePaginationData}
          paginationValues={{
            limit: Number(limit),
            offset: Number(offset),
            total: legalEntitiesSummaryTableData?.investorHouseholdSummary?.total || 0
          }}
          refetch={() => null as any}
        />
      </PaginationWrap>
    </>
  );
};

export default AllEntitiesSummaryTable;

const TableSumFooterWrapper = styled.div`
  &.sticky {
    position: sticky;
    bottom: 0;
    z-index: 9;
  }
`;

const PaginationWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
